import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Save, Plus, Settings, ChevronDown, ChevronUp, Upload,
  Trash2, Loader2, ArrowLeft, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Perfectionnement professionnel',
  'Management',
  'Pédagogie',
  'Communication',
  'Leadership',
  'Développement personnel',
  'Accompagnement',
  'Formation de formateurs',
  'Numérique',
  'Autre'
];

const COLORS = [
  '#22c55e', '#ec4899', '#eab308', '#f97316', '#3b82f6',
  '#8b5cf6', '#ef4444', '#14b8a6', '#6366f1', '#94a3b8'
];

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-slate-100 dark:border-slate-800">
      <button
        className="w-full flex items-center justify-between py-5 px-1 text-left hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors"
        onClick={() => setOpen(!open)}
        type="button"
        data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <span className="font-medium text-slate-800 dark:text-slate-200">{title}</span>
        {open ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
      </button>
      {open && <div className="pb-6 px-1">{children}</div>}
    </div>
  );
};

const ProgrammeEditPage = () => {
  const { programmeId } = useParams();
  const navigate = useNavigate();
  const isNew = programmeId === 'nouveau';
  const photoRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);

  const [form, setForm] = useState({
    title: '', subtitle: '', internal_code: '', category: '',
    color: '#14b8a6', version: 1, version_date: '',
    duration_hours: 0, duration_days: 0, photo_url: '',
    pricing_enterprise: 0, pricing_individual: 0, pricing_independent: 0,
    published_online: false, cpf_eligible: false, competency_block: '',
    modalities: '', training_action_type: '', description: '',
    pedagogical_objectives: '', learner_profile: '', training_content: '',
    execution_monitoring: '', certification_modalities: '',
    quality: '', accounting_config: '', competency_domains: ''
  });

  useEffect(() => {
    if (!isNew) {
      fetch(`${API}/programmes/${programmeId}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setForm(prev => ({ ...prev, ...d })); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [programmeId, isNew]);

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/programmes` : `${API}/programmes/${programmeId}`;
      const method = isNew ? 'POST' : 'PUT';
      const res = await fetch(url, {
        method, credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? 'Programme créé' : 'Programme enregistré');
        if (isNew) navigate(`/bibliotheque/programmes/${data.programme_id}`, { replace: true });
        else navigate(`/bibliotheque/programmes/${programmeId}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch (e) { toast.error('Erreur de connexion'); }
    setSaving(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isNew) { toast.error("Enregistrez d'abord le programme"); return; }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await fetch(`${API}/programmes/${programmeId}/photo`, {
        method: 'POST', credentials: 'include', body: formData
      });
      if (res.ok) {
        const data = await res.json();
        update('photo_url', data.photo_url);
        toast.success('Photo mise à jour');
      } else {
        toast.error('Erreur upload (max 500 Ko)');
      }
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return (
    <Layout><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div></Layout>
  );

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl" data-testid="programme-edit-page">
        {/* Breadcrumb */}
        <div className="text-sm text-slate-500 flex items-center gap-1">
          <button onClick={() => navigate('/bibliotheque/programmes')} className="hover:text-teal-600 flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Bibliothèque
          </button>
          <span className="mx-1">&gt;</span>
          {isNew ? (
            <span className="text-teal-600 font-medium">Nouveau programme</span>
          ) : (
            <>
              <button onClick={() => navigate(`/bibliotheque/programmes/${programmeId}`)} className="hover:text-teal-600 truncate max-w-xs">
                {form.title || '...'}
              </button>
              <span className="mx-1">&gt;</span>
              <span className="text-teal-600 font-medium">Modifier mon programme de formation</span>
            </>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-slate-900">
          {isNew ? 'Nouveau programme de formation' : form.title}
        </h1>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <Button
            className="bg-teal-500 hover:bg-teal-600 text-white"
            onClick={handleSave}
            disabled={saving}
            data-testid="save-programme-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
          <Button variant="outline" onClick={() => navigate(isNew ? '/bibliotheque/programmes' : `/bibliotheque/programmes/${programmeId}`)}>
            Annuler
          </Button>
        </div>

        {/* Main form card */}
        <Card>
          <CardContent className="p-6 space-y-6">
            {/* Title + Photo row */}
            <div className="flex gap-6">
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Titre du programme</label>
                  <Input
                    value={form.title}
                    onChange={(e) => update('title', e.target.value)}
                    placeholder="Titre du programme"
                    data-testid="input-title"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Sous-titre</label>
                  <Input
                    value={form.subtitle}
                    onChange={(e) => update('subtitle', e.target.value)}
                    placeholder="Sous-titre"
                    data-testid="input-subtitle"
                  />
                </div>
              </div>
              {/* Photo upload */}
              <div className="w-48 shrink-0">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Photo du programme</label>
                <div className="border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-3 text-center">
                  {form.photo_url ? (
                    <img src={form.photo_url} alt="" className="w-full h-24 object-cover rounded-lg mb-2" />
                  ) : (
                    <div className="w-full h-24 bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center mb-2">
                      <Image className="h-8 w-8 text-slate-300" />
                    </div>
                  )}
                  <p className="text-[10px] text-slate-400 mb-2">JPG, PNG ou WebP, ≤ 500Ko</p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" size="sm" className="text-xs h-7" onClick={() => photoRef.current?.click()} data-testid="upload-photo-btn">
                      Charger
                    </Button>
                    {form.photo_url && (
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => update('photo_url', '')}>
                        <Trash2 className="h-3 w-3 text-red-400" />
                      </Button>
                    )}
                  </div>
                  <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                </div>
              </div>
            </div>

            {/* Code + Category row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Code interne</label>
                <Input
                  value={form.internal_code}
                  onChange={(e) => update('internal_code', e.target.value)}
                  placeholder="Auto-généré si vide"
                  data-testid="input-code"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Catégorie</label>
                <Select value={form.category} onValueChange={(v) => update('category', v)}>
                  <SelectTrigger data-testid="select-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Color picker */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Couleur</label>
              <div className="flex gap-2">
                {COLORS.map(c => (
                  <button
                    key={c}
                    type="button"
                    className={cn("w-7 h-7 rounded-full transition-transform", form.color === c && "ring-2 ring-offset-2 ring-slate-400 scale-110")}
                    style={{ backgroundColor: c }}
                    onClick={() => update('color', c)}
                  />
                ))}
              </div>
            </div>

            {/* Version + Duration row */}
            <div className="grid grid-cols-5 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Version</label>
                <Input type="number" min={1} value={form.version} onChange={(e) => update('version', parseInt(e.target.value) || 1)} data-testid="input-version" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Date de la version</label>
                <Input type="date" value={form.version_date} onChange={(e) => update('version_date', e.target.value)} data-testid="input-version-date" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Durée (heures)</label>
                <Input type="number" step="0.5" min={0} value={form.duration_hours} onChange={(e) => update('duration_hours', parseFloat(e.target.value) || 0)} data-testid="input-hours" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Durée (jours)</label>
                <Input type="number" step="0.5" min={0} value={form.duration_days} onChange={(e) => update('duration_days', parseFloat(e.target.value) || 0)} data-testid="input-days" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Variantes</label>
                <Input type="number" min={0} value={form.variants_count} onChange={(e) => update('variants_count', parseInt(e.target.value) || 0)} />
              </div>
            </div>

            {/* Pricing */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Prix de la formation</label>
              <div className="grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 mb-1">Entreprise</p>
                    <Input
                      type="number" min={0} step={10}
                      value={form.pricing_enterprise}
                      onChange={(e) => update('pricing_enterprise', parseFloat(e.target.value) || 0)}
                      className="text-center font-semibold"
                      data-testid="input-price-enterprise"
                    />
                    <p className="text-xs text-slate-400 mt-1">EUR</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 mb-1">Particulier</p>
                    <Input
                      type="number" min={0} step={10}
                      value={form.pricing_individual}
                      onChange={(e) => update('pricing_individual', parseFloat(e.target.value) || 0)}
                      className="text-center font-semibold"
                      data-testid="input-price-individual"
                    />
                    <p className="text-xs text-slate-400 mt-1">EUR</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800">
                  <div className="text-center flex-1">
                    <p className="text-xs text-slate-500 mb-1">Indépendant</p>
                    <Input
                      type="number" min={0} step={10}
                      value={form.pricing_independent}
                      onChange={(e) => update('pricing_independent', parseFloat(e.target.value) || 0)}
                      className="text-center font-semibold"
                      data-testid="input-price-independent"
                    />
                    <p className="text-xs text-slate-400 mt-1">EUR</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={form.published_online}
                  onCheckedChange={(v) => update('published_online', !!v)}
                  data-testid="check-published"
                />
                <label className="text-sm text-slate-700">Publier sur le catalogue en ligne</label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={form.cpf_eligible}
                  onCheckedChange={(v) => update('cpf_eligible', !!v)}
                  data-testid="check-cpf"
                />
                <label className="text-sm text-slate-700">Eligible CPF (Compte Personnel Formation)</label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Collapsible sections */}
        <Card>
          <CardContent className="p-6 pt-0">
            <CollapsibleSection title="Modalités">
              <Textarea
                rows={4}
                placeholder="Modalités de la formation (présentiel, distanciel, mixte...)"
                value={form.modalities}
                onChange={(e) => update('modalities', e.target.value)}
                data-testid="input-modalities"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Type d'action de formation">
              <Textarea
                rows={3}
                placeholder="Action de formation, bilan de compétences, VAE..."
                value={form.training_action_type}
                onChange={(e) => update('training_action_type', e.target.value)}
                data-testid="input-training-action"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Description du programme">
              <Textarea
                rows={6}
                placeholder="Description détaillée du programme de formation..."
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
                data-testid="input-description"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Objectifs pédagogiques">
              <Textarea
                rows={5}
                placeholder="Lister les objectifs pédagogiques de la formation..."
                value={form.pedagogical_objectives}
                onChange={(e) => update('pedagogical_objectives', e.target.value)}
                data-testid="input-objectives"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Profil des apprenants">
              <Textarea
                rows={4}
                placeholder="Public cible, prérequis, niveau requis..."
                value={form.learner_profile}
                onChange={(e) => update('learner_profile', e.target.value)}
                data-testid="input-learner-profile"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Contenu de la formation (progression pédagogique)">
              <Textarea
                rows={6}
                placeholder="Détail des modules, progression pédagogique, planning..."
                value={form.training_content}
                onChange={(e) => update('training_content', e.target.value)}
                data-testid="input-training-content"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Suivi de l'exécution">
              <Textarea
                rows={4}
                placeholder="Feuille d'émargement, certificat de réalisation, attestation..."
                value={form.execution_monitoring}
                onChange={(e) => update('execution_monitoring', e.target.value)}
                data-testid="input-execution"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Modalités de certification">
              <Textarea
                rows={4}
                placeholder="Modalités d'évaluation, certification, diplôme..."
                value={form.certification_modalities}
                onChange={(e) => update('certification_modalities', e.target.value)}
                data-testid="input-certification"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Qualité">
              <Textarea
                rows={4}
                placeholder="Indicateurs qualité, taux de satisfaction, amélioration continue..."
                value={form.quality}
                onChange={(e) => update('quality', e.target.value)}
                data-testid="input-quality"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Configuration comptable du produit">
              <Textarea
                rows={3}
                placeholder="Code comptable, TVA, plan comptable..."
                value={form.accounting_config}
                onChange={(e) => update('accounting_config', e.target.value)}
                data-testid="input-accounting"
              />
            </CollapsibleSection>

            <CollapsibleSection title="Domaines de compétences">
              <Textarea
                rows={4}
                placeholder="Blocs de compétences, référentiel, RNCP..."
                value={form.competency_domains}
                onChange={(e) => update('competency_domains', e.target.value)}
                data-testid="input-competency"
              />
            </CollapsibleSection>
          </CardContent>
        </Card>

        {/* Bottom save button */}
        <Button
          className="bg-teal-500 hover:bg-teal-600 text-white"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-programme-bottom"
        >
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Enregistrer
        </Button>
      </div>
    </Layout>
  );
};

export default ProgrammeEditPage;
